import { CommonModule, isPlatformBrowser } from '@angular/common';
import { Component, ElementRef, OnDestroy, OnInit, PLATFORM_ID, ViewChild, inject } from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { environment } from '../../../../../environments/environment';

type ChatRole = 'user' | 'assistant' | 'system';

export interface ChatMedia {
  url: string;
  title?: string;
  type?: 'movie' | 'tv' | 'person' | string;
}

export interface ChatMessage {
  role: ChatRole;
  content: string;
  cleanContent?: string;
  isStreaming?: boolean;
  media?: ChatMedia | null;
}

export interface IncomingSocketPayload {
  type?: string;
  content?: string;
  suggestions?: string[];
  media_image?: {
    image_url: string;
    title?: string;
    type?: string;
  } | null;
}

interface OutgoingSocketPayload {
  type: 'user_message' | 'clear_context';
  content?: string;
}

@Component({
  selector: 'app-chatting',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule],
  templateUrl: './chatting.component.html',
  styleUrl: './chatting.component.scss'
})
export class Chatting implements OnInit, OnDestroy {
  @ViewChild('chatScroll') chatScroll?: ElementRef<HTMLDivElement>;

  readonly messageControl = new FormControl('', { nonNullable: true });

  sessionId = '';
  messages: ChatMessage[] = [];
  suggestions: string[] = [];
  isConnected = false;
  isConnecting = false;
  isThinking = false;
  connectionError = '';

  private socket: WebSocket | null = null;
  private isDestroyed = false;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private readonly reconnectDelayMs = 2200;
  private readonly pendingMessages: OutgoingSocketPayload[] = [];
  private readonly platformId = inject(PLATFORM_ID);

  ngOnInit(): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    this.sessionId = this.getOrCreateSessionId();
    this.connectSocket();
  }

  ngOnDestroy(): void {
    this.isDestroyed = true;
    this.clearReconnectTimer();
    if (this.socket) {
      this.socket.close();
      this.socket = null;
    }
  }

  sendMessage(): void {
    const content = this.messageControl.value.trim();
    if (!content) {
      return;
    }

    this.messages.push({ role: 'user', content });
    this.messageControl.setValue('');
    this.suggestions = [];
    this.sendOrQueue({ type: 'user_message', content });
    this.scrollToBottom();
  }

  sendSuggestedQuestion(question: string): void {
    this.messageControl.setValue(question);
    this.sendMessage();
  }

  clearChatContext(): void {
    this.suggestions = [];
    this.messages = [];
    this.isThinking = false;
    this.sendOrQueue({ type: 'clear_context' });
  }

  reconnect(): void {
    this.connectionError = '';
    this.clearReconnectTimer();
    this.socket?.close();
    this.socket = null;
    this.connectSocket();
  }

  handleComposerKeydown(event: KeyboardEvent): void {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      this.sendMessage();
    }
  }

  trackByMessage(index: number): number {
    return index;
  }

  private connectSocket(): void {
    if (!isPlatformBrowser(this.platformId) || this.isConnecting || this.isDestroyed) {
      return;
    }

    if (this.socket && this.socket.readyState === WebSocket.OPEN) {
      this.isConnected = true;
      this.connectionError = '';
      this.flushPendingMessages();
      return;
    }

    if (this.socket && this.socket.readyState === WebSocket.CONNECTING) {
      this.isConnecting = true;
      return;
    }

    this.isConnecting = true;
    this.isConnected = false;
    this.isThinking = false;
    this.clearReconnectTimer();

    const wsUrl = this.buildSocketUrl(this.sessionId || this.getOrCreateSessionId());
    this.socket = new WebSocket(wsUrl);

    this.socket.onopen = () => {
      this.isConnecting = false;
      this.isConnected = true;
      this.connectionError = '';
      this.flushPendingMessages();
    };

    this.socket.onmessage = (event: MessageEvent<string>) => {
      this.handleSocketMessage(event.data);
    };

    this.socket.onerror = () => {
      this.isConnecting = false;
      this.isConnected = false;
      if (!this.isDestroyed) {
        this.connectionError = 'Unable to connect to chat server.';
      }
    };

    this.socket.onclose = () => {
      this.isConnecting = false;
      this.isConnected = false;
      this.isThinking = false;
      this.socket = null;

      if (!this.isDestroyed) {
        this.connectionError = 'Connection closed. Reconnecting...';
        this.scheduleReconnect();
      }
    };
  }

  private handleSocketMessage(raw: string): void {
    let payload: IncomingSocketPayload;
    try {
      payload = JSON.parse(raw) as IncomingSocketPayload;
    } catch {
      return;
    }

    const messageType = payload.type ?? '';

    if (messageType === 'thinking_indicator') {
      this.isThinking = true;
      this.scrollToBottom();
      return;
    }

    if (messageType === 'bot_message_chunk') {
      this.isThinking = false;
      this.appendAssistantChunk(payload.content ?? '');
      return;
    }

    if (messageType === 'bot_message_complete') {
      this.isThinking = false;
      this.completeAssistantMessage(payload.content ?? '', payload.media_image);
      this.scrollToBottom();
      return;
    }

    if (messageType === 'suggested_questions') {
      this.suggestions = payload.suggestions ?? [];
      return;
    }

    if (messageType === 'cleared') {
      this.suggestions = [];
      this.messages = [];
      this.isThinking = false;
      return;
    }
  }

  onMediaImageError(msg: ChatMessage): void {
    if (msg.media) {
      msg.media = null;
    }
  }

  private extractMediaFromContent(text: string): { cleanText: string; media: ChatMedia | null } {
    if (!text) {
      return { cleanText: '', media: null };
    }
    // Match markdown image ![title](https://image.tmdb.org/...)
    const mdImgRegex = /!\[(.*?)\]\((https?:\/\/image\.tmdb\.org\/[^\s\)]+)\)/;
    const match = text.match(mdImgRegex);
    if (match) {
      const alt = (match[1] || '').trim();
      const url = match[2];
      const cleanText = text.replace(match[0], '').replace(/\n\s*\n\s*\n/g, '\n\n').trim();
      const altLower = alt.toLowerCase();
      const isPerson = altLower.includes('actor') || altLower.includes('person') || altLower.includes('photo');
      const isTv = altLower.includes('tv') || altLower.includes('series');
      return {
        cleanText,
        media: {
          url,
          title: alt,
          type: isPerson ? 'person' : (isTv ? 'tv' : 'movie')
        }
      };
    }
    return { cleanText: text, media: null };
  }

  private appendAssistantChunk(chunk: string): void {
    if (!chunk) {
      return;
    }

    const lastMessage = this.messages[this.messages.length - 1];
    if (lastMessage && lastMessage.role === 'assistant' && lastMessage.isStreaming) {
      lastMessage.content += chunk;
      const extracted = this.extractMediaFromContent(lastMessage.content);
      lastMessage.cleanContent = extracted.cleanText;
      if (extracted.media && !lastMessage.media) {
        lastMessage.media = extracted.media;
      }
    } else {
      const extracted = this.extractMediaFromContent(chunk);
      this.messages.push({
        role: 'assistant',
        content: chunk,
        cleanContent: extracted.cleanText,
        media: extracted.media,
        isStreaming: true
      });
    }

    this.scrollToBottom();
  }

  private completeAssistantMessage(fullContent: string, mediaPayload?: IncomingSocketPayload['media_image']): void {
    const lastMessage = this.messages[this.messages.length - 1];
    const content = fullContent || (lastMessage ? lastMessage.content : '');
    const extracted = this.extractMediaFromContent(content);

    let media = extracted.media;
    if (!media && mediaPayload && mediaPayload.image_url) {
      media = {
        url: mediaPayload.image_url,
        title: mediaPayload.title || '',
        type: mediaPayload.type || 'movie'
      };
    }

    if (lastMessage && lastMessage.role === 'assistant') {
      lastMessage.content = content;
      lastMessage.cleanContent = extracted.cleanText;
      if (media) {
        lastMessage.media = media;
      }
      lastMessage.isStreaming = false;
      return;
    }

    if (content) {
      this.messages.push({
        role: 'assistant',
        content: content,
        cleanContent: extracted.cleanText,
        media: media,
        isStreaming: false
      });
    }
  }

  private sendJson(payload: OutgoingSocketPayload): void {
    if (this.socket?.readyState !== WebSocket.OPEN) {
      return;
    }

    this.socket.send(JSON.stringify(payload));
  }

  private sendOrQueue(payload: OutgoingSocketPayload): void {
    if (this.socket?.readyState === WebSocket.OPEN) {
      this.connectionError = '';
      this.sendJson(payload);
      return;
    }

    this.pendingMessages.push(payload);
    this.connectionError = this.isConnecting
      ? 'Connecting... your message will be sent automatically.'
      : 'Socket is disconnected. Reconnecting...';
    this.connectSocket();
  }

  private flushPendingMessages(): void {
    if (this.socket?.readyState !== WebSocket.OPEN || !this.pendingMessages.length) {
      return;
    }

    while (this.pendingMessages.length) {
      const nextPayload = this.pendingMessages.shift();
      if (nextPayload) {
        this.sendJson(nextPayload);
      }
    }
  }

  private getOrCreateSessionId(): string {
    const storageKey = 'cinema_chat_session_id';
    const existingSession = sessionStorage.getItem(storageKey);
    if (existingSession) {
      return existingSession;
    }

    const randomPart =
      typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
        ? crypto.randomUUID()
        : `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;

    const newSessionId = `chat-${randomPart}`;
    sessionStorage.setItem(storageKey, newSessionId);
    return newSessionId;
  }

  private buildSocketUrl(sessionId: string): string {
    const socketUrl = environment.socketUrl;
    
    const wsBaseUrl = socketUrl.replace(/\/$/, '');
    return `${wsBaseUrl}/ws/${encodeURIComponent(sessionId)}`;
  }

  private scheduleReconnect(): void {
    if (this.reconnectTimer || this.isDestroyed) {
      return;
    }

    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      this.connectSocket();
    }, this.reconnectDelayMs);
  }

  private clearReconnectTimer(): void {
    if (!this.reconnectTimer) {
      return;
    }

    clearTimeout(this.reconnectTimer);
    this.reconnectTimer = null;
  }

  private scrollToBottom(): void {
    setTimeout(() => {
      if (!this.chatScroll) {
        return;
      }
      const element = this.chatScroll.nativeElement;
      element.scrollTop = element.scrollHeight;
    }, 0);
  }
}
